// The shape of a day in one sentence, and the smallest thing in it. Both are
// what Today needs to answer "can I face this?" without counting anything
// undone: they describe the work, they do not score it.
import type { TaskResource } from '@/kinds/task/apply'
import { fmtMinutes } from '@/lib/time'

/** Minutes of work on the day, from the estimates that exist. */
export const workMinutes = (tasks: TaskResource[]): { minutes: number; unestimated: number } => {
  let minutes = 0
  let unestimated = 0
  for (const t of tasks) {
    const e = t.spec.estimate_minutes
    if (e) minutes += e
    else unestimated++
  }
  return { minutes, unestimated }
}

/**
 * One line describing the day: how much work is on it and how long is left
 * before the evening. `until` is the recap time (HH:MM) when one is set.
 */
/** The shape of the day as separate phrases, for a layout that sets them apart rather than joining them. */
export const dayShapeParts = (
  tasks: TaskResource[],
  now: number,
  until: string | null | undefined,
  next?: { title: string; minutes: number },
): string[] | null => {
  const span = (left: number) => {
    const hours = Math.floor(left / 60)
    const mins = left % 60
    return hours ? `${hours}h${mins ? ` ${mins}m` : ''}` : `${mins}m`
  }
  // the next fixed thing is the nearer edge of the day than the evening
  const ahead = next && next.minutes > 0 ? `${next.title} in ${span(next.minutes)}` : null
  const { minutes, unestimated } = workMinutes(tasks)
  // with nothing estimated there is no shape of work to describe, only a count
  // of undone things, which is the one thing this app never shows
  if (!tasks.length || !minutes) return ahead ? [ahead] : null
  const parts = [`${fmtMinutes(minutes)} of work`]
  if (unestimated) parts.push(`${unestimated} without an estimate`)
  const left = minutesUntil(until, now)
  if (ahead && (left === null || next!.minutes < left)) parts.push(ahead)
  else if (left !== null && left > 0) parts.push(`${span(left)} until the evening`)
  return parts
}

/** The same as one plain sentence (a label, a push), the phrases joined by commas, never a dot. */
export const dayShape = (
  tasks: TaskResource[],
  now: number,
  until: string | null | undefined,
  next?: { title: string; minutes: number },
): string | null => dayShapeParts(tasks, now, until, next)?.join(', ') ?? null

/** Minutes from now until a local HH:MM today, or null when it has passed. */
export const minutesUntil = (hhmm: string | null | undefined, now: number): number | null => {
  if (!hhmm) return null
  const [h, m] = hhmm.split(':').map(Number)
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return null
  const at = new Date(now)
  at.setHours(h, m, 0, 0)
  const mins = Math.round((at.getTime() - now) / 60_000)
  return mins > 0 ? mins : null
}

/** The smallest thing on the day: what to keep when everything is too much. */
export const smallest = (tasks: TaskResource[]): TaskResource | undefined =>
  [...tasks].sort((a, b) => {
    const ea = a.spec.estimate_minutes ?? 999
    const eb = b.spec.estimate_minutes ?? 999
    if (ea !== eb) return ea - eb
    return a.id < b.id ? -1 : 1
  })[0]
